import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ALL_SEASONS, type ChatMessage } from '@gl/shared';

import { useChats } from '../api/hooks.js';
import { useCan, useCurrentUser } from '../auth.js';
import { SeasonSelector } from '../components/SeasonSelector.js';
import { EmptyState, ErrorState, Loading, LoginPrompt } from '../components/states.js';
import { useOwnSleeperId } from '../components/ownTeam.js';
import { formatExactTime, formatLongDay, formatTimeOfDay, leagueDayKey } from '../util/format.js';

/**
 * The league chat, read back.
 *
 * A mirror of the conversation on Sleeper, taken by the nightly job. There is
 * nothing here to type into: the chat happens over there, and a second place to
 * post would only split it in two. What this adds is the thing Sleeper is worst
 * at — finding the message you half remember from two seasons ago.
 */

const seasonLabel = (season: string) => (season === ALL_SEASONS ? 'All Seasons' : season);

/** Long enough that typing a word does not fire a request per letter. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The search term picked out of a message.
 *
 * Case-insensitive, like the search itself, and split rather than replaced so
 * nothing here interprets what someone typed as markup.
 */
function Highlighted({ text, term }: { text: string; term: string }) {
  if (!term) return <>{text}</>;

  const parts: Array<{ text: string; hit: boolean }> = [];
  const haystack = text.toLowerCase();
  const needle = term.toLowerCase();
  let at = 0;

  for (;;) {
    const found = haystack.indexOf(needle, at);
    if (found === -1) break;
    if (found > at) parts.push({ text: text.slice(at, found), hit: false });
    parts.push({ text: text.slice(found, found + needle.length), hit: true });
    at = found + needle.length;
  }
  if (at === 0) return <>{text}</>;
  if (at < text.length) parts.push({ text: text.slice(at), hit: false });

  return (
    <>
      {parts.map((part, index) =>
        part.hit ? (
          <mark key={index} className="bg-brass-400 text-field-950">
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}

function Message({
  message,
  previous,
  term,
  mine,
}: {
  message: ChatMessage;
  previous: ChatMessage | undefined;
  term: string;
  mine: string | null;
}) {
  // A run of messages from one person reads as one turn of the conversation, so
  // only the first of them carries a name.
  const sameSpeaker =
    previous !== undefined &&
    previous.authorName === message.authorName &&
    leagueDayKey(previous.postedAt) === leagueDayKey(message.postedAt);

  const own = mine !== null && message.authorId === mine;

  return (
    <li data-message={message.id} className={`px-3 ${sameSpeaker ? 'py-0.5' : 'pb-0.5 pt-3'}`}>
      {sameSpeaker ? null : (
        <p className="flex items-baseline gap-2">
          <span
            className={`font-display text-sm font-semibold uppercase tracking-tight ${
              own ? 'text-brass-300' : 'text-field-50'
            }`}
          >
            {message.authorName}
          </span>
          {message.isBot ? (
            <span className="badge border border-field-700 text-field-500">Sleeper</span>
          ) : null}
          <time
            className="font-display text-[0.6875rem] uppercase tracking-bug text-field-600"
            dateTime={message.postedAt}
            title={formatExactTime(message.postedAt)}
          >
            {formatTimeOfDay(message.postedAt)}
          </time>
        </p>
      )}

      <p
        className={`whitespace-pre-wrap break-words text-[0.9375rem] leading-relaxed ${
          message.isBot ? 'text-field-400' : 'text-field-200'
        }`}
      >
        <Highlighted text={message.body} term={term} />
        {message.attachment ? (
          <span className="ml-2 font-display text-[0.6875rem] uppercase tracking-bug text-field-500">
            [{message.attachment}]
          </span>
        ) : null}
      </p>
    </li>
  );
}

/**
 * Deliberately not sticky. A search turns the list into one message per day,
 * and ten dividers taking it in turns to hang over the message above them is
 * worse than simply scrolling past them.
 */
function DayDivider({ iso }: { iso: string }) {
  return (
    <li className="px-3 pt-3">
      <p className="border-b border-field-800 pb-1 font-display text-[0.6875rem] uppercase tracking-bug text-field-500">
        {formatLongDay(iso)}
      </p>
    </li>
  );
}

export function ChatsPage() {
  const user = useCurrentUser();
  const canRead = useCan('read_chats');
  const mine = useOwnSleeperId();

  const [params, setParams] = useSearchParams();
  const season = params.get('season') ?? '';
  const [typed, setTyped] = useState(params.get('q') ?? '');
  const [search, setSearch] = useState(typed);

  // The query follows the box a beat behind, so a word costs one request.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (search) updated.set('q', search);
        else updated.delete('q');
        return updated;
      },
      { replace: true },
    );
  }, [search, setParams]);

  const chats = useChats(season, search, Boolean(user) && canRead);
  const pages = chats.data?.pages ?? [];
  const first = pages[0];

  // Pages arrive newest-block first; the conversation reads the other way.
  const messages = useMemo(
    () => [...pages].reverse().flatMap((page) => page.messages),
    [pages],
  );

  const scroller = useRef<HTMLDivElement>(null);
  const settled = useRef(false);
  /** The message the reader was looking at when they asked for older ones. */
  const anchor = useRef<{ id: string; offset: number } | null>(null);

  const offsetOf = (element: HTMLElement, id: string): number | null => {
    const node = element.querySelector<HTMLElement>(`[data-message="${CSS.escape(id)}"]`);
    return node ? node.getBoundingClientRect().top - element.getBoundingClientRect().top : null;
  };

  /**
   * Two scroll behaviours, and they pull in opposite directions.
   *
   * Arriving, the reader wants the end of the conversation, the way any chat
   * opens. Loading older messages, they want to stay exactly where they were
   * while a few hundred older ones appear above them — so the message they were
   * looking at is pinned back to where it was, rather than the scroll position
   * being computed from a height that is still settling.
   */
  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element || messages.length === 0) return;

    const pinned = anchor.current;
    if (pinned) {
      anchor.current = null;
      const now = offsetOf(element, pinned.id);
      if (now !== null) element.scrollTop += now - pinned.offset;
      return;
    }

    if (!settled.current) {
      element.scrollTop = element.scrollHeight;
      settled.current = true;
    }
  }, [messages]);

  // A new season or a new search is a new conversation to arrive at.
  useEffect(() => {
    settled.current = false;
  }, [season, search]);

  const loadOlder = () => {
    const element = scroller.current;
    const top = messages[0]?.id;
    const offset = element && top ? offsetOf(element, top) : null;
    anchor.current = top && offset !== null ? { id: top, offset } : null;
    void chats.fetchNextPage();
  };

  if (!user) {
    return (
      <div>
        <div className="page-head">
          <p className="eyebrow mb-1">The league</p>
          <h1 className="page-title">Chats</h1>
        </div>
        <LoginPrompt>Log in to read the league chat.</LoginPrompt>
      </div>
    );
  }

  return (
    <div>
      <div className="page-head flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <p className="eyebrow mb-1">The league</p>
          <h1 className="page-title">Chats</h1>
          <p className="mt-2 max-w-2xl text-sm text-field-400">
            The league chat on Sleeper, mirrored by the nightly update. Read-only — say it over
            there, find it here.
          </p>
        </div>
        {first?.syncedAt ? (
          <p className="pt-1 font-display text-[0.6875rem] uppercase tracking-bug text-field-600 sm:text-right">
            Mirrored {formatExactTime(first.syncedAt)}
          </p>
        ) : null}
      </div>

      {!canRead ? (
        <EmptyState>
          Reading the league chat needs the “Read the league chat” permission. Ask an admin to turn
          it on for you.
        </EmptyState>
      ) : (
        <>
          {first && first.seasons.length > 0 ? (
            <SeasonSelector
              seasons={[...first.seasons, ALL_SEASONS]}
              // Every season at once is the default here, unlike the stats and
              // standings pages: a conversation is one long thread, and the
              // thing anyone comes to search for is rarely in the season they
              // happen to be in.
              active={season || ALL_SEASONS}
              label={seasonLabel}
              onSelect={(next) => {
                const updated = new URLSearchParams(params);
                updated.set('season', next);
                setParams(updated, { replace: true });
              }}
              hint="Left and right change the season"
              aside={
                <span className="font-display text-xs uppercase tracking-bug text-field-500">
                  {first.total.toLocaleString('en-US')} message
                  {first.total === 1 ? '' : 's'}
                  {search ? ' match' : ''}
                </span>
              }
            />
          ) : null}

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="chat-search">
              Search the chat
            </label>
            <input
              id="chat-search"
              type="search"
              className="input max-w-md"
              placeholder="Search the chat…"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
            {typed ? (
              <button type="button" className="btn-secondary" onClick={() => setTyped('')}>
                Clear
              </button>
            ) : null}
          </div>

          {chats.isPending ? <Loading /> : null}
          {chats.error ? <ErrorState error={chats.error} action="Log in to read the chat." /> : null}

          {first?.empty ? (
            <EmptyState>
              No chat has been mirrored yet. The nightly update needs a Sleeper session to read it —
              Sleeper publishes no public endpoint for league chat.
            </EmptyState>
          ) : null}

          {first && !first.empty && messages.length === 0 ? (
            <EmptyState>
              {search ? `Nothing matches “${search}”.` : 'No messages in this season.'}
            </EmptyState>
          ) : null}

          {messages.length > 0 ? (
            <div className="card p-0">
              <div ref={scroller} className="max-h-[70vh] overflow-y-auto py-2">
                <ul>
                  {chats.hasNextPage ? (
                    <li className="px-3 py-2">
                      <button
                        type="button"
                        className="btn-secondary w-full"
                        disabled={chats.isFetchingNextPage}
                        onClick={loadOlder}
                      >
                        {chats.isFetchingNextPage ? 'Loading…' : 'Load older messages'}
                      </button>
                    </li>
                  ) : (
                    <li className="px-3 py-2 text-center font-display text-[0.6875rem] uppercase tracking-bug text-field-600">
                      The beginning of the chat
                    </li>
                  )}

                  {messages.map((message, index) => {
                    const previous = messages[index - 1];
                    const newDay =
                      previous === undefined ||
                      leagueDayKey(previous.postedAt) !== leagueDayKey(message.postedAt);

                    return (
                      <Fragment key={message.id}>
                        {newDay ? <DayDivider iso={message.postedAt} /> : null}
                        <Message
                          message={message}
                          previous={newDay ? undefined : previous}
                          term={search}
                          mine={mine}
                        />
                      </Fragment>
                    );
                  })}
                </ul>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
